import { Text } from '@amzn/meridian';
import { ErrorAlert } from 'components/ErrorAlert';
import { OverlayWrapper } from 'components/OverlayWrapper';
import { find, isEmpty } from 'es-toolkit/compat';
import { type AssetModel, type AssetSearchResult, useProjectAssetsByIdsQuery } from 'generated/graphql';
import stringify from 'json-stable-stringify';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { classes, stylesheet } from 'typestyle';
import { FormLabelWithOptional, type FormLabelWithOptionalProps } from '../../components/form/FormLabelWithOptional';
import { ImageLightbox } from './ImageLightbox';
import type { ImageSelectorModalProps } from './ImageSelectorModal';
import { type AssetError, ImageSelectorModalContainer, type ImageSelectorModalContainerProps } from './ImageSelectorModalContainer';
import { JImage, type JImageProps } from './JImage';
import { JImageMenu } from './JImageMenu';

export type ImageFormInputClassNames = 'root' | 'assetContainer' | 'selectImageButton';

export interface ImageFormInputProps
  extends Pick<ImageSelectorModalProps, 'projectId' | 'filterParams' | 'visibleTabs' | 'language' | 'validFileTypes'>,
    Pick<ImageSelectorModalContainerProps, 'uploadFileToS3'>,
    FormLabelWithOptionalProps,
    Pick<JImageProps, 'type'> {
  // the "value" of the image input
  value: AssetModel | null;
  // when the user selects a new image
  onChange: (value: AssetModel | null) => void;
  onBlur?: () => void;
  onFocus?: () => void;

  disabled: boolean;
  invalid: boolean;
  invalidFeedback?: string;
  canRemove: boolean;
  isLowRes: boolean;
  recommendedMaxSize?: number;
  containerClassName?: string;
  overrideClasses?: Partial<Record<ImageFormInputClassNames, string>>;
  iconLabel?: string;
  disableHoverOverlay?: boolean;
  /** Must have `position: "absolute"` to work! */
  imageOverlay?: ReactNode;
  initialZoomFactor?: number;
  allowScroll?: boolean;
}

const css = stylesheet({
  assetContainer: {
    display: 'inline-block',
    position: 'relative',
  },
  borderDanger: {
    border: '2px solid #dc3545',
    borderRadius: 1,
  },
  label: {
    fontStyle: 'italic',
  },
  selectImagePrompt: {
    height: '300px',
    width: '100%',
  },
});

/**
 * This is a control that lets the user pick an image from the image selector modal. It displays the image chosen.
 * It is suitable for putting into a form that needs to gather an image from the user.
 */
export const ImageFormInput: FC<ImageFormInputProps> = ({
  projectId,
  filterParams,
  visibleTabs,
  validFileTypes,
  language,
  value,
  disabled,
  invalid,
  invalidFeedback,
  canRemove,
  onChange,
  uploadFileToS3,
  onBlur,
  onFocus,
  recommendedMaxSize,
  label,
  optional,
  containerClassName,
  iconLabel,
  overrideClasses: cssOverrides,
  type,
  isLowRes,
  disableHoverOverlay,
  imageOverlay,
  initialZoomFactor,
  allowScroll,
}) => {
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [imageSelectorVisible, setImageSelectorVisible] = useState(false);
  const {
    data: projectAssetsInCampaign,
    loading,
    error,
  } = useProjectAssetsByIdsQuery({
    skip: !value?.assetId,
    variables: {
      input: {
        assetIds: value?.assetId
          ? [
              value?.assetId,
            ]
          : [],
        projectId,
      },
    },
  });

  const projectAssets = useMemo(
    () => projectAssetsInCampaign?.getAssetsByIds.assets ?? [],
    [
      projectAssetsInCampaign?.getAssetsByIds.assets,
    ],
  );

  const asset = useMemo(
    () => find(projectAssets, (asset) => asset.assetId === value?.assetId),
    [
      projectAssets,
      value,
    ],
  );

  // Use the full-size image if available, fallback to potentially compressed s3SignedUrlGet, fallback to empty string (no image)
  const imageSrc = asset?.originalAssetS3Get ?? asset?.s3SignedUrlGet ?? '';

  const convertToAssetModel = useCallback(
    (asset: AssetSearchResult): AssetModel => ({
      alt: asset.name,
      assetId: asset.assetId,
      isLowRes,
      name: asset.name,
      origHeight: asset.assetMetadata?.height ?? 100,
      origWidth: asset.assetMetadata?.width ?? 100,
      size: asset.size!,
      width: asset.assetMetadata?.width ?? 100,
    }),
    [
      isLowRes,
    ],
  );

  const assetModelFromSearch = useMemo(() => {
    if (asset) {
      return convertToAssetModel(asset);
    }
  }, [
    asset,
    convertToAssetModel,
  ]);

  const onAssetChange = useCallback(
    (asset: AssetModel) => {
      onChange(asset);
      onBlur?.();
    },
    [
      onBlur,
      onChange,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (assetModelFromSearch && stringify(value) !== stringify(assetModelFromSearch)) {
      onAssetChange(assetModelFromSearch);
    }
  }, [
    assetModelFromSearch,
  ]);

  const onViewFullSize = () => {
    if (asset) {
      setLightboxVisible(true);
    }
  };

  const onLightboxHidden = () => {
    setLightboxVisible(false);
  };

  const onAssetsReady = async (assets: AssetSearchResult[], errors: AssetError[]) => {
    if (!isEmpty(errors)) {
      // don't close the error modal if there were errors
      return;
    }
    setImageSelectorVisible(false);
    // this is a single-select input, so there will be only one asset
    onAssetChange(convertToAssetModel(assets[0]));
  };

  const onReplaceImage = () => {
    setImageSelectorVisible(true);
  };

  const onRemoveImage = () => {
    onChange(null);
    onBlur?.();
  };

  const warningMessage =
    recommendedMaxSize && (asset?.size ?? 0) > recommendedMaxSize
      ? `Warning: image exceeds maximum weight guidelines of ${recommendedMaxSize / 1000} KB`
      : '';
  const weightDescription = asset?.size ? `Weight: ${Math.ceil(asset.size / 1000)} KB. ${warningMessage}` : 'Weight not available.';
  const sizeDescription =
    asset?.assetMetadata?.width && asset?.assetMetadata?.height
      ? `Size: ${asset.assetMetadata.width} x ${asset.assetMetadata.height}.`
      : 'Size not available.';

  return (
    <div className={classes(containerClassName, cssOverrides?.root)} data-cy="asset-container">
      {value && imageSrc && asset ? (
        <div
          className={classes(css.assetContainer, cssOverrides?.assetContainer, {
            [css.borderDanger]: invalid,
          })}
        >
          <JImage alt={value.alt} fluid height={value.origHeight} loading={loading} selectable={false} src={imageSrc} type={type}>
            {!disableHoverOverlay && (
              <JImageMenu
                canRemove={canRemove}
                disabled={disabled}
                onRemove={onRemoveImage}
                onReplace={onReplaceImage}
                onViewFullSize={onViewFullSize}
              />
            )}
          </JImage>
          {!!imageOverlay && <OverlayWrapper>{imageOverlay}</OverlayWrapper>}
        </div>
      ) : (
        <Button
          className={classes(css.selectImagePrompt, cssOverrides?.selectImageButton, {
            [css.borderDanger]: invalid,
          })}
          data-cy="select-img-btn"
          disabled={disabled}
          onBlur={onBlur}
          onClick={onReplaceImage}
          onFocus={onFocus}
          type="button"
          variant="outline-secondary"
        >
          <i className="fa fa-4x fa-upload" />
          <div className="prompt">{iconLabel}</div>
        </Button>
      )}
      {invalid && (
        <Text color="error" data-cy="invalid-feedback" type="b200">
          {invalidFeedback}
        </Text>
      )}

      {asset && imageSrc && (
        <Text color={warningMessage ? 'error' : 'primary'} data-cy="image-size-message" type="b200">
          {`${sizeDescription} ${weightDescription}`}
        </Text>
      )}

      {lightboxVisible && asset && imageSrc && (
        <ImageLightbox
          allowScroll={allowScroll}
          height={asset.assetMetadata?.height ?? 100}
          initialZoomFactor={initialZoomFactor}
          onHidden={onLightboxHidden}
          src={imageSrc}
          title={asset.name}
          visible={lightboxVisible}
          width={asset.assetMetadata?.width ?? 100}
        />
      )}

      <ImageSelectorModalContainer
        filterParams={filterParams}
        language={language}
        multiselect={false}
        onAssetsReady={onAssetsReady}
        onHide={() => setImageSelectorVisible(false)}
        projectId={projectId}
        uploadFileToS3={uploadFileToS3}
        validFileTypes={validFileTypes}
        visible={imageSelectorVisible}
        visibleTabs={visibleTabs}
      />

      {error && <ErrorAlert contextMessage="Could not load project images" error={error} />}
      <FormLabelWithOptional className={`d-block, ${css.label}`} label={label} optional={optional} />
    </div>
  );
};
